import { useTranslation } from 'react-i18next';
import { Container, Stepper, StepperProps } from '@mantine/core';
import { useFormContext } from 'react-hook-form';
import { StepsName } from '../types';
import { XIcon } from '../../../icons';
import { SmallStepper } from '../../../components/shared/SmallStepper';

export type FormStepperProps = Omit<StepperProps, 'children'> & {
  steps: StepsName[];
  active: number;
  setStep: (step: number) => void;
  small?: boolean;
};

/**
 * FormStepper component for large and middle screens
 * @private
 *
 * @param active - active step
 * @param steps - array of steps
 * @param setStep - function to set active step
 * @param small - if true, stepper will be small
 * @param props - rest of stepper props
 */
export const FormStepper: React.FC<FormStepperProps> = ({
  active,
  steps,
  setStep,
  small = false,
  ...props
}) => {
  const { t } = useTranslation('forms');
  const {
    formState: { errors },
  } = useFormContext();

  const formErrors = Object.keys(errors).includes('contractValues');
  const attachmentsErrors = Object.keys(errors).includes('fileAttachments');
  const sendErrors = Object.keys(errors).some(key =>
    ['name', 'surname', 'email', 'otp', 'phone', 'terms'].includes(key),
  );

  const stepLabels = {
    form: t('external.stepDocumentLabel'),
    attachments: t('external.stepAttachmentsLabel'),
    otp: t('external.stepSendLabel'),
  };

  const currentStepLabel = stepLabels[steps[active]];
  const nextStepLabel = stepLabels[steps[active + 1]];

  return small ? (
    <SmallStepper
      currentStepIndex={active}
      nextStepTitle={nextStepLabel}
      currentStepTitle={currentStepLabel}
      stepsCount={steps.length}
    />
  ) : (
    <Container size={steps.length === 2 ? 'sm' : 'lg'}>
      <Stepper active={active} onStepClick={setStep} {...props}>
        {steps.includes('form') && (
          <Stepper.Step
            label={t('external.stepDocumentLabel')}
            description={t('external.stepDocumentDescription')}
            allowStepSelect={active >= 0}
            color={formErrors ? 'red' : undefined}
            completedIcon={formErrors ? <XIcon color="#fff" /> : undefined}
          />
        )}

        {steps.includes('attachments') && (
          <Stepper.Step
            label={t('external.stepAttachmentsLabel')}
            description={t('external.stepAttachmentsDescription')}
            allowStepSelect={active >= 1}
            color={attachmentsErrors ? 'red' : undefined}
            completedIcon={
              attachmentsErrors ? <XIcon color="#fff" /> : undefined
            }
          />
        )}

        {steps.includes('otp') && (
          <Stepper.Step
            label={t('external.stepSendLabel')}
            description={t('external.stepSendDescription')}
            allowStepSelect={active >= 2}
            color={sendErrors ? 'red' : undefined}
            completedIcon={sendErrors ? <XIcon color="#fff" /> : undefined}
          />
        )}
      </Stepper>
    </Container>
  );
};
